// Copyright 2025, University of Maryland, All Rights Reserved
import { LearningObjectives } from './LearningObjectives'
import { LearningObjectivesPointsChart } from './LearningObjectivesPointsChart'
import { LearningObjectivesCoverageChart } from './LearningObjectivesCoverageChart'

interface SecondaryObjective {
  objective_statement: string
  concepts_covered?: string[]
  points_allocated?: number
  problems_aligned?: string[]
}

interface LearningObjective {
  objective_statement: string
  bloom_level?: string
  concepts_covered?: string[]
  problems_aligned?: string[]
  points_allocated?: number
  coverage_quality?: string
  coverage_depth?: 'H' | 'M' | 'L'
  coverage_depth_rationale?: string
  secondary_objectives?: SecondaryObjective[]
}

interface LearningObjectivesSimplifiedProps {
  objectives: LearningObjective[]
  documentTitle?: string
  documentCount?: number
}

export function LearningObjectivesSimplified({
  objectives,
  documentTitle,
  documentCount,
}: LearningObjectivesSimplifiedProps) {
  if (!objectives || objectives.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
        <span className="text-4xl mb-3 block">📋</span>
        <p className="text-gray-500">No learning outcomes found in this result.</p>
      </div>
    )
  }

  const hasCoverageDepth = objectives.some(o => o.coverage_depth)
  const hasPoints = objectives.some(o => o.points_allocated && o.points_allocated > 0)

  return (
    <div className="space-y-6">
      {documentTitle && (
        <div className="flex items-center gap-3 pb-2 border-b border-gray-200">
          <span className="text-2xl">📄</span>
          <h3 className="text-lg font-semibold text-gray-800">{documentTitle}</h3>
        </div>
      )}

      <LearningObjectives objectives={objectives} />

      {hasCoverageDepth ? (
        <LearningObjectivesCoverageChart
          objectives={objectives}
          documentCount={documentCount}
        />
      ) : hasPoints ? (
        <LearningObjectivesPointsChart
          objectives={objectives}
          documentCount={documentCount}
        />
      ) : null}
    </div>
  )
}
